import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { brand } from "../../../config/theme";
import ReportActionButton from "../components/ReportActionButton";
import ReportLoadingPanel from "../components/ReportLoadingPanel";
import { ReportStatCard } from "../components/ReportCharts";
import type { ReportTableColumn } from "../components/ReportPointsTable";
import { exportHistoricToExcel, exportHistoricToPdf } from "../utils/exportHistoric";
import { exportRows, fetchMonthlyMatrix, fmtValue, heat, totalOf, valueOf, type Metric } from "./monthlyMatrix";

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const WEEKDAY = ["D", "L", "M", "M", "J", "V", "S"];

/**
 * Resumen mensual (backlog de Mapon): unidades × días del mes con km u horas de
 * manejo, desde los resúmenes diarios guardados. Hoy no aparece (todavía no
 * tiene resumen guardado); para hoy están los Informes rápidos.
 */
export default function MonthlySection() {
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const companyId = useCompaniesStore((s) => s.companyId);
  const [month, setMonth] = useState(thisMonth);
  const [asked, setAsked] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>("km");
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["reports", "monthly", companyId, asked],
    queryFn: () => fetchMonthlyMatrix(companyId!, asked!),
    enabled: !!companyId && !!asked,
    staleTime: 5 * 60_000,
  });

  const max = useMemo(() => {
    if (!data) return 0;
    let m = 0;
    for (const r of data.rows) for (const d of data.days) m = Math.max(m, valueOf(r, d, metric) ?? 0);
    return m;
  }, [data, metric]);

  const totals = useMemo(() => {
    if (!data) return null;
    const sum = data.rows.reduce((n, r) => n + totalOf(r, metric), 0);
    const active = data.rows.filter((r) => r.active_days > 0).length;
    return { sum, active, avg: active ? sum / active : 0 };
  }, [data, metric]);

  const onDownload = async (kind: "pdf" | "excel") => {
    if (!data) return;
    setExporting(kind);
    try {
      const columns: ReportTableColumn<Record<string, string>>[] = [
        { key: "unidad", header: "Unidad" },
        ...data.days.map((d) => ({ key: d, header: d.slice(8), align: "right" as const })),
        { key: "total", header: "Total", align: "right" },
      ];
      const meta = {
        title: metric === "km" ? "Kilómetros por día" : "Horas de manejo por día",
        period: `Mes ${data.month} (zona ${data.tz})`,
        fileName: `resumen_mensual_${metric}_${data.month}.${kind === "pdf" ? "pdf" : "xlsx"}`,
      };
      const rows = exportRows(data, metric);
      if (kind === "pdf") exportHistoricToPdf(rows, columns, meta);
      else await exportHistoricToExcel(rows, columns, meta);
    } finally {
      setExporting(null);
    }
  };

  const muted = themeClasses.textSecondary;
  const withUnit = (v: number) => `${metric === "km" ? Math.round(v).toLocaleString("es-MX") : fmtValue(v, metric)} ${metric === "km" ? "km" : "h"}`;
  const cellBorder = isDarkMode ? "border-dark-border" : "border-gray-200";
  const stickyBg = isDarkMode ? "bg-dark-bg" : "bg-white";

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4 flex flex-col gap-4">
      <div className={`flex flex-col sm:flex-row sm:items-end justify-between gap-3 rounded-lg border p-3 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
        <div className="flex flex-wrap items-end gap-3">
          <label className={`flex flex-col gap-1 text-xs ${themeClasses.text}`}>
            Mes
            <input
              type="month"
              value={month}
              max={thisMonth()}
              onChange={(e) => setMonth(e.target.value)}
              className={`rounded-md border px-2 py-1.5 text-sm ${isDarkMode ? "bg-white/5 border-dark-border text-white" : "bg-white border-gray-300"}`}
            />
          </label>
          <div role="tablist" aria-label="Qué mostrar" className={`flex p-0.5 rounded-lg ${isDarkMode ? "bg-white/5" : "bg-gray-100"}`}>
            {([
              ["km", "Kilómetros"],
              ["hours", "Horas de manejo"],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={metric === id}
                onClick={() => setMetric(id)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${metric === id ? (isDarkMode ? "bg-dark-card text-white shadow" : "bg-white text-gray-900 shadow") : muted}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <ReportActionButton type="button" onClick={() => setAsked(month)} disabled={!companyId || !month || isFetching}>
          Consultar
        </ReportActionButton>
      </div>

      {!companyId && <p className={`text-sm ${muted}`}>Selecciona una compañía.</p>}
      {isFetching && <ReportLoadingPanel isDarkMode={isDarkMode} icon="ri-calendar-2-line" title="Armando el mes…" subtitle="Leyendo los resúmenes diarios guardados" />}
      {isError && <p className="text-sm text-red-500">{(error as { response?: { data?: { message?: string } } })?.response?.data?.message || "No se pudo cargar el resumen mensual."}</p>}

      {data && !isFetching && totals && (
        <div className={`flex flex-col gap-4 rounded-lg border p-3 sm:p-4 ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${themeClasses.text}`}>
              {data.rows.length} unidad(es) con datos · {data.days.length} día(s)
              <span className={muted}> · hoy no se incluye (está en Informes rápidos)</span>
            </span>
            {data.rows.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(["pdf", "excel"] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => onDownload(kind)}
                    disabled={exporting !== null}
                    className={`text-xs px-3 py-1 rounded ${themeClasses.buttonBg} text-white ${themeClasses.buttonHover2} disabled:opacity-50`}
                  >
                    {exporting === kind ? "Generando..." : `Descargar ${kind === "pdf" ? "PDF" : "Excel"}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
            <ReportStatCard icon={metric === "km" ? "ri-road-map-line" : "ri-time-line"} label={metric === "km" ? "Km de la flota" : "Horas de manejo de la flota"} value={withUnit(totals.sum)} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-truck-line" label="Unidades que se movieron" value={String(totals.active)} isDarkMode={isDarkMode} />
            <ReportStatCard icon="ri-bar-chart-line" label="Promedio por unidad activa" value={withUnit(totals.avg)} isDarkMode={isDarkMode} />
          </div>

          {data.rows.length === 0 ? (
            <p className={`text-sm ${muted}`}>Sin resúmenes guardados para ese mes.</p>
          ) : (
            <div className="w-full overflow-auto rounded-lg border max-h-[65vh]">
              <table className="text-[11px] border-collapse">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-primary-500 text-white">
                    <th className="sticky left-0 z-30 bg-primary-500 p-2 text-left font-bold min-w-40">Unidad</th>
                    {data.days.map((d) => (
                      <th key={d} className="p-1.5 font-semibold text-center min-w-9">
                        <span className="block leading-tight">{Number(d.slice(8))}</span>
                        <span className="block leading-tight opacity-70 font-normal">{WEEKDAY[new Date(`${d}T12:00:00`).getDay()]}</span>
                      </th>
                    ))}
                    <th className="p-2 font-bold text-right min-w-16">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.device_id} className={themeClasses.text}>
                      <td className={`sticky left-0 z-10 p-2 border-b ${cellBorder} ${stickyBg} whitespace-nowrap`}>
                        <span className="font-medium">{r.unit_name}</span>
                        {r.plate && <span className={`ml-1 ${muted}`}>{r.plate}</span>}
                      </td>
                      {data.days.map((d) => {
                        const v = valueOf(r, d, metric);
                        const h = heat(v, max);
                        return (
                          <td
                            key={d}
                            className={`p-1.5 text-center tabular-nums border-b border-l ${cellBorder}`}
                            style={h > 0 ? { backgroundColor: `color-mix(in srgb, ${brand.primary} ${Math.round(12 + h * 58)}%, transparent)` } : undefined}
                            title={`${r.unit_name} · ${d}: ${fmtValue(v, metric) || "sin resumen"}`}
                          >
                            {fmtValue(v, metric)}
                          </td>
                        );
                      })}
                      <td className={`p-2 text-right font-semibold tabular-nums border-b border-l ${cellBorder}`}>{fmtValue(totalOf(r, metric), metric)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
